import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArticleIcon from '@mui/icons-material/Article';
import TuneIcon from '@mui/icons-material/Tune';
import InsightsIcon from '@mui/icons-material/Insights';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate } from 'react-router-dom';
import { audiencesApi } from '../../services/audiences';
import { proposedArticlesApi } from '../../services/proposed-articles';
import { useToast } from '../../contexts/ToastContext';
import type {
  Audience,
  MeasurementSynthesis as Synthesis,
  MeasurementRecommendation,
  MeasurementFact,
  Slice,
} from '@traaker/shared';
import DomainFavicon from '../../components/competitors/DomainFavicon';
import { EngineLogo, ENGINE_META, engineKey } from '../../components/brand/EngineLogo';
import { Flag, ZoneFlag } from '../dashboard/Flag';
import { languageFlag, resolveZone } from '../dashboard/geoFlags';

let languageDisplayNames: Intl.DisplayNames | undefined;
/** Human language name from an ISO 639-1 code (e.g. "fr" → "French"/"français"
 *  depending on the runtime locale) — mirrors geoFlags.ts's use of
 *  Intl.DisplayNames for countries. Falls back to the raw code. */
function languageName(code: string): string {
  try {
    languageDisplayNames ??= new Intl.DisplayNames([navigator.language || 'fr'], { type: 'language' });
    return languageDisplayNames.of(code.toLowerCase()) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** Coverage KPI tile. */
function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 130 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

/** Thin SVG donut for the 4-way coverage split — rounded caps, a 2px surface
 *  gap between arcs (mirrors the CoverageBar's stacked-segment convention). */
function CoverageDonut({
  covered,
  leaked,
  mentioned,
  notCovered,
}: {
  covered: number;
  leaked: number;
  mentioned: number;
  notCovered: number;
}) {
  const total = covered + leaked + mentioned + notCovered || 1;
  const R = 34;
  const CX = 40;
  const CY = 40;
  const CIRC = 2 * Math.PI * R;
  const gap = 2; // px gap between arcs
  const segs: { value: number; color: string }[] = [
    { value: covered, color: '#2e7d32' },
    { value: leaked, color: '#ed6c02' },
    { value: mentioned, color: '#0288d1' },
    { value: notCovered, color: '#bdbdbd' },
  ].filter((s) => s.value > 0);
  let offset = 0;
  const arcs = segs.map((s, i) => {
    const len = (s.value / total) * CIRC - (segs.length > 1 ? gap : 0);
    const dasharray = `${Math.max(len, 0)} ${CIRC - Math.max(len, 0)}`;
    const el = (
      <circle
        key={i}
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke={s.color}
        strokeWidth={10}
        strokeLinecap="round"
        strokeDasharray={dasharray}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${CX} ${CY})`}
      />
    );
    offset += (s.value / total) * CIRC;
    return el;
  });
  const pctCovered = Math.round((covered / total) * 100);
  return (
    <Box sx={{ position: 'relative', width: 80, height: 80 }}>
      <svg width={80} height={80} viewBox="0 0 80 80">
        {arcs}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography variant="body2" fontWeight={700}>{pctCovered}%</Typography>
      </Box>
    </Box>
  );
}

/** Stacked coverage bar for one slice row (mirrors MeasurementResults.CoverageBar). */
function SliceBar({ s }: { s: Slice }) {
  const total = s.total || 1;
  const seg = (n: number, color: string) =>
    n > 0 ? <Box sx={{ width: `${(n / total) * 100}%`, bgcolor: color, height: 8 }} /> : null;
  return (
    <Box sx={{ display: 'flex', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
      {seg(s.covered, '#2e7d32')}
      {seg(s.leaked, '#ed6c02')}
      {seg(s.mentioned, '#0288d1')}
      {seg(s.notCovered, '#bdbdbd')}
    </Box>
  );
}

const SLICE_TABS: { key: keyof NonNullable<Synthesis['sliceScores']>; label: string }[] = [
  { key: 'bySegment', label: 'Segment' },
  { key: 'byEngine', label: 'Moteur' },
  { key: 'byZone', label: 'Zone' },
  { key: 'byLanguage', label: 'Langue' },
  { key: 'byCatalogue', label: 'Catalogue' },
];

/** Row identity for a slice: a visual (flag/logo) instead of raw text where
 *  the dimension has one — engine logo, language flag, zone country flag.
 *  Segment/catalogue have no such visual, so they stay text-only. */
function SliceIdentity({ dimension, s }: { dimension: keyof NonNullable<Synthesis['sliceScores']>; s: Slice }) {
  if (dimension === 'byEngine') {
    const label = ENGINE_META[engineKey(s.key)].label;
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <EngineLogo engine={s.key} size={18} />
        <Typography variant="caption" noWrap title={label}>
          {label}
        </Typography>
      </Stack>
    );
  }
  if (dimension === 'byLanguage') {
    const label = languageName(s.key);
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Flag countryCode={languageFlag(s.key)} size={18} title={label} />
        <Typography variant="caption" noWrap title={label}>
          {label}
        </Typography>
      </Stack>
    );
  }
  if (dimension === 'byZone') {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <ZoneFlag zone={resolveZone(s.key)} size={18} title={s.label} />
        <Typography variant="caption" noWrap title={s.label}>
          {s.label}
        </Typography>
      </Stack>
    );
  }
  return (
    <Typography variant="caption" noWrap title={s.label}>
      {s.label}
    </Typography>
  );
}

/** Analysis-by-dimension block: chip-switched slices, each a stacked coverage
 *  bar + avg-score dot, sorted by volume. */
function SliceAnalysis({ sliceScores }: { sliceScores: NonNullable<Synthesis['sliceScores']> }) {
  const available = SLICE_TABS.filter((t) => (sliceScores[t.key]?.length ?? 0) > 1);
  const [tab, setTab] = useState(available[0]?.key);
  if (!available.length) return null;
  const active = tab ?? available[0].key;
  const slices = sliceScores[active] || [];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Analyse par segment / moteur / zone / langue / catalogue
      </Typography>
      <Tabs value={active} onChange={(_, v) => setTab(v)} sx={{ mb: 1, minHeight: 32 }}>
        {available.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} sx={{ minHeight: 32, py: 0.5 }} />
        ))}
      </Tabs>
      <Stack spacing={1}>
        {slices.slice(0, 12).map((s) => (
          <Stack key={s.key} direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              <SliceIdentity dimension={active} s={s} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SliceBar s={s} />
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={`${s.avgScore}`}
              sx={{ minWidth: 44 }}
              color={s.avgScore >= 60 ? 'success' : s.avgScore >= 30 ? 'warning' : 'default'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ width: 50, textAlign: 'right' }}>
              {s.total}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** `**bold**` → <strong>, everything else as plain text — a 10-line helper,
 *  not a markdown lib (item g's narrative-highlight instruction). */
function renderHighlighted(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

const REC_META: Record<
  MeasurementRecommendation['target'],
  { label: string; color: 'primary' | 'secondary' | 'info'; icon: React.ReactNode }
> = {
  content: { label: 'Contenu', color: 'secondary', icon: <ArticleIcon sx={{ fontSize: 16 }} /> },
  product: { label: 'Fiche produit', color: 'primary', icon: <TuneIcon sx={{ fontSize: 16 }} /> },
  positioning: { label: 'Positionnement', color: 'info', icon: <InsightsIcon sx={{ fontSize: 16 }} /> },
};
const LEVEL_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

/** Deterministic coverage tallies over an arbitrary set of fact rows — the
 *  same shape as `MeasurementSynthesis.totals`, computed client-side so the
 *  filter bar (item l) recomputes KPIs instantly without a server round-trip. */
function factsToTotals(facts: MeasurementFact[]): Synthesis['totals'] {
  let brandCovered = 0;
  let assortmentLeaked = 0;
  let mentioned = 0;
  let notCovered = 0;
  let scoreSum = 0;
  for (const f of facts) {
    if (f.coverage === 'brand_covered') brandCovered++;
    else if (f.coverage === 'assortment_leaked') assortmentLeaked++;
    else if (f.coverage === 'mentioned') mentioned++;
    else notCovered++;
    scoreSum += f.score || 0;
  }
  const total = facts.length;
  return { total, brandCovered, assortmentLeaked, mentioned, notCovered, avgScore: total ? Math.round(scoreSum / total) : 0 };
}

/** Group fact rows by a key extractor into a coverage-tallied Slice[] — the
 *  client-side counterpart of the API's `sliceBy` (item l). */
function factsToSlices(
  facts: MeasurementFact[],
  keyOf: (f: MeasurementFact) => string,
  labelOf: (key: string) => string,
): Slice[] {
  const agg = new Map<
    string,
    { total: number; covered: number; leaked: number; mentioned: number; notCovered: number; scoreSum: number }
  >();
  for (const f of facts) {
    const key = keyOf(f) || '(inconnu)';
    const a = agg.get(key) ?? { total: 0, covered: 0, leaked: 0, mentioned: 0, notCovered: 0, scoreSum: 0 };
    a.total++;
    if (f.coverage === 'brand_covered') a.covered++;
    else if (f.coverage === 'assortment_leaked') a.leaked++;
    else if (f.coverage === 'mentioned') a.mentioned++;
    else a.notCovered++;
    a.scoreSum += f.score || 0;
    agg.set(key, a);
  }
  return [...agg.entries()]
    .map(([key, a]) => ({
      key,
      label: labelOf(key),
      total: a.total,
      avgScore: a.total ? Math.round(a.scoreSum / a.total) : 0,
      covered: a.covered,
      leaked: a.leaked,
      mentioned: a.mentioned,
      notCovered: a.notCovered,
    }))
    .sort((a, b) => b.total - a.total);
}

const FACT_COVERAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'brand_covered', label: 'Recommandé' },
  { value: 'assortment_leaked', label: 'Fuite' },
  { value: 'mentioned', label: 'Mentionné sans lien' },
  { value: 'not_covered', label: 'Absent' },
];

/** Generic multi-select filter chip group (Segment/Moteur/Langue/Zone/Couverture). */
function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string[];
  options: { value: string; label: string }[];
  onChange: (next: string[]) => void;
}) {
  if (options.length < 2) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(sel) => (sel as string[]).length}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * Run "Synthèse" tab — deterministic coverage stats + LLM-authored, prioritized
 * recommendations (uncited product families, competitor patterns, quick wins).
 * Cached server-side (computed once at first open / regenerated on demand).
 *
 * Layout (item g): (1) KPI tiles + coverage donut, (2) analysis by dimension
 * (deterministic slices), (3) narrative with highlights, (4) evidence as
 * cards, (5) recommendations LAST (they conclude the read).
 *
 * Item l: a filter bar (Segment/Moteur/Langue/Zone/Couverture) over a minimal
 * fact-row projection recomputes (1) and (2) client-side for ANY filter
 * combination, instant, no re-scan. The server-computed `sliceScores` render
 * first (no flash) and are used verbatim while no filter is active; the
 * narrative/recommendations/evidence stay run-global (LLM-cached) regardless.
 */
export function MeasurementSynthesis({
  brandId,
  audience,
  runId,
}: {
  brandId: string;
  audience: Audience;
  runId: string;
}) {
  const audienceId = audience.audienceId;
  const { show } = useToast();
  const navigate = useNavigate();
  const [syn, setSyn] = useState<Synthesis | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [savedIdeas, setSavedIdeas] = useState<Set<string>>(new Set());
  const [facts, setFacts] = useState<MeasurementFact[] | null>(null);
  const [fSegments, setFSegments] = useState<string[]>([]);
  const [fEngines, setFEngines] = useState<string[]>([]);
  const [fLanguages, setFLanguages] = useState<string[]>([]);
  const [fZones, setFZones] = useState<string[]>([]);
  const [fCoverages, setFCoverages] = useState<string[]>([]);

  const load = (force = false) => {
    setLoading(!force);
    setRegenerating(force);
    audiencesApi
      .runSynthesis(brandId, audienceId, runId, force)
      .then(setSyn)
      .catch(() => show('Synthèse indisponible', { severity: 'error' }))
      .finally(() => {
        setLoading(false);
        setRegenerating(false);
      });
  };

  useEffect(() => {
    load();
    setFacts(null);
    setFSegments([]);
    setFEngines([]);
    setFLanguages([]);
    setFZones([]);
    setFCoverages([]);
    audiencesApi
      .runFacts(brandId, audienceId, runId)
      .then(setFacts)
      .catch(() => setFacts(null)); // best-effort — filter bar just stays hidden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, audienceId, runId]);

  const segmentName = (id: string) => audience.segments.find((s) => s.segmentId === id)?.name || id;

  const filterOptions = useMemo(() => {
    if (!facts) return null;
    const opt = (vals: Iterable<string>, labelOf: (v: string) => string) =>
      [...new Set(vals)].map((v) => ({ value: v, label: labelOf(v) }));
    return {
      segments: opt(facts.map((f) => f.segmentId), segmentName),
      engines: opt(facts.map((f) => f.engine), (e) => ENGINE_META[engineKey(e)].label),
      languages: opt(facts.map((f) => f.language), languageName),
      zones: opt(facts.map((f) => f.zone).filter((z): z is string => !!z), (z) => z),
      coverages: FACT_COVERAGE_OPTIONS.filter((o) => facts.some((f) => f.coverage === o.value)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facts]);

  const hasActiveFilters =
    fSegments.length > 0 || fEngines.length > 0 || fLanguages.length > 0 || fZones.length > 0 || fCoverages.length > 0;

  const filteredFacts = useMemo(() => {
    if (!facts || !hasActiveFilters) return null;
    return facts.filter(
      (f) =>
        (!fSegments.length || fSegments.includes(f.segmentId)) &&
        (!fEngines.length || fEngines.includes(f.engine)) &&
        (!fLanguages.length || fLanguages.includes(f.language)) &&
        (!fZones.length || (f.zone && fZones.includes(f.zone))) &&
        (!fCoverages.length || fCoverages.includes(f.coverage)),
    );
  }, [facts, hasActiveFilters, fSegments, fEngines, fLanguages, fZones, fCoverages]);

  // Effective totals/slices: filtered client-side recompute when a filter is
  // active, else the exact server-computed values (no flash, no drift).
  const effectiveTotals = filteredFacts ? factsToTotals(filteredFacts) : syn?.totals;
  const effectiveSliceScores = filteredFacts
    ? {
        bySegment: factsToSlices(filteredFacts, (f) => f.segmentId, segmentName),
        byEngine: factsToSlices(filteredFacts, (f) => f.engine, (k) => k.toUpperCase()),
        byLanguage: factsToSlices(filteredFacts, (f) => f.language, (k) => k.toUpperCase()),
        byZone: factsToSlices(filteredFacts, (f) => f.zone || '(sans zone)', (k) => k),
        // Catalogue isn't in the fact projection (item l scope: Segment/Moteur/
        // Langue/Zone/Couverture only) — hide that tab while filtered.
        byCatalogue: [] as Slice[],
      }
    : syn?.sliceScores;

  const clearFilters = () => {
    setFSegments([]);
    setFEngines([]);
    setFLanguages([]);
    setFZones([]);
    setFCoverages([]);
  };

  const saveIdea = async (title: string, detail: string) => {
    try {
      await proposedArticlesApi.createContentTopic({
        brandId,
        topic: detail ? `${title} — ${detail}` : title,
        originLabel: 'Visibilité GEO',
      });
      setSavedIdeas((s) => new Set(s).add(title));
      show('Idée ajoutée à la création de contenu', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Ajout impossible', { severity: 'error' });
    }
  };

  const narrativeNodes = useMemo(
    () => (syn?.narrative ? renderHighlighted(syn.narrative) : null),
    [syn?.narrative],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!syn || syn.totals.total === 0) {
    return <Typography color="text.secondary">Aucun résultat à synthétiser.</Typography>;
  }

  const t = effectiveTotals || syn.totals;
  const pct = (n: number) => (t.total ? Math.round((n / t.total) * 100) : 0);

  return (
    <Stack spacing={2.5}>
      {/* 0. Filter bar (item l) — client-side recompute of (1) and (2) below */}
      {filterOptions && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <FilterSelect label="Segment" value={fSegments} options={filterOptions.segments} onChange={setFSegments} />
            <FilterSelect label="Moteur" value={fEngines} options={filterOptions.engines} onChange={setFEngines} />
            <FilterSelect label="Langue" value={fLanguages} options={filterOptions.languages} onChange={setFLanguages} />
            <FilterSelect label="Zone" value={fZones} options={filterOptions.zones} onChange={setFZones} />
            <FilterSelect
              label="Couverture"
              value={fCoverages}
              options={filterOptions.coverages}
              onChange={setFCoverages}
            />
            {hasActiveFilters && (
              <Chip
                size="small"
                variant="outlined"
                label={`${filteredFacts?.length ?? 0} / ${facts?.length ?? 0} réponses — réinitialiser`}
                onDelete={clearFilters}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* 1. KPI row + coverage donut */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="stretch">
        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CoverageDonut
            covered={t.brandCovered}
            leaked={t.assortmentLeaked}
            mentioned={t.mentioned}
            notCovered={t.notCovered}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Répartition de la couverture
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              <LegendDot color="#2e7d32" label={`Recommandé (${t.brandCovered})`} />
              <LegendDot color="#ed6c02" label={`Fuite (${t.assortmentLeaked})`} />
              <LegendDot color="#0288d1" label={`Mentionné sans lien (${t.mentioned})`} />
              <LegendDot color="#bdbdbd" label={`Absent (${t.notCovered})`} />
            </Stack>
          </Box>
        </Paper>
        <Kpi label="Score de visibilité moyen" value={`${t.avgScore}`} sub="/ 100" color="#2E7D32" />
        <Kpi label="Marque recommandée" value={`${pct(t.brandCovered)}%`} sub={`${t.brandCovered} réponses`} color="#2e7d32" />
        <Kpi label="Fuite d'assortiment" value={`${pct(t.assortmentLeaked)}%`} sub={`${t.assortmentLeaked} réponses`} color="#ed6c02" />
        <Kpi label="Mentionné sans lien" value={`${pct(t.mentioned)}%`} sub={`${t.mentioned} réponses`} color="#0288d1" />
        <Kpi label="Non couvert" value={`${pct(t.notCovered)}%`} sub={`${t.notCovered} réponses`} color="#9e9e9e" />
      </Stack>

      {/* 2. Analysis by dimension (deterministic slices) */}
      {effectiveSliceScores && <SliceAnalysis sliceScores={effectiveSliceScores} />}

      {/* 3. Narrative, with highlights */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AutoAwesomeIcon color="secondary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Analyse & priorités
          </Typography>
          {hasActiveFilters && (
            <Typography variant="caption" color="text.secondary">
              analyse globale du run
            </Typography>
          )}
          <Tooltip title="Recalculer la synthèse">
            <span>
              <Button
                size="small"
                startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={() => load(true)}
                disabled={regenerating}
              >
                Recalculer
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {narrativeNodes ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {narrativeNodes}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {syn.llmOk
              ? 'Aucune synthèse générée.'
              : "L'analyse rédigée n'a pas pu être générée — les statistiques ci-dessous restent exactes."}
          </Typography>
        )}
      </Paper>

      {/* 4a. Uncited product families — card grid */}
      {syn.uncitedClusters.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Familles de produits où vous êtes absent
            </Typography>
            {hasActiveFilters && (
              <Typography variant="caption" color="text.secondary">
                (analyse globale du run)
              </Typography>
            )}
          </Stack>
          <Grid container spacing={1.5}>
            {syn.uncitedClusters.map((c, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Tooltip title={c.samplePrompts[0] ? `« ${c.samplePrompts[0]} »` : ''} placement="top" arrow>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                        {c.label}
                      </Typography>
                      <Chip size="small" color="warning" label={c.missCount} />
                    </Stack>
                    {c.skus.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Proche : {c.skus.map((s) => s.name).slice(0, 2).join(' · ')}
                      </Typography>
                    )}
                  </Paper>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 4b. Quick wins — table with score-gap progress bars */}
      {syn.quickWins.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <BoltIcon fontSize="small" sx={{ color: '#ed6c02' }} />
            <Typography variant="subtitle2">Gains rapides — proche de gagner</Typography>
          </Stack>
          <Paper variant="outlined">
            <Stack divider={<Divider />}>
              {syn.quickWins.slice(0, 8).map((q) => (
                <Box key={`${q.promptId}#${q.engine}`} sx={{ p: 1.25 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap title={q.promptText}>
                      {q.promptText}
                    </Typography>
                    <Chip
                      size="small"
                      color={q.coverage === 'assortment_leaked' ? 'warning' : q.coverage === 'mentioned' ? 'info' : 'default'}
                      label={q.coverage === 'assortment_leaked' ? 'Fuite' : q.coverage === 'mentioned' ? 'Mentionné sans lien' : 'Absent'}
                    />
                    {q.bestPosition && <Chip size="small" variant="outlined" label={`#${q.bestPosition}`} />}
                  </Stack>
                  {q.closestSku && (
                    <Typography variant="caption" color="text.secondary">
                      Produit à pousser : {q.closestSku.name}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* 4c. Influential media — chip cloud with favicon, sized by frequency */}
      {syn.topCompetitors.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
            Médias & sources influents
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Domaines cités par les IA qui pèsent sur ces réponses
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            {syn.topCompetitors.map((c) => {
              const maxCount = syn.topCompetitors[0]?.count || 1;
              const scale = 0.85 + 0.3 * (c.count / maxCount);
              return (
                <Chip
                  key={c.domain}
                  size="small"
                  variant="outlined"
                  icon={<DomainFavicon domain={c.domain} name={c.domain} size={16} />}
                  label={`${c.domain} · ${c.count}×`}
                  sx={{ transform: `scale(${scale})`, transformOrigin: 'left center' }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* 5. Recommendations LAST — they conclude the read */}
      {syn.recommendations.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recommandations ({syn.recommendations.length})
          </Typography>
          <Stack spacing={1}>
            {syn.recommendations.map((r, i) => {
              const meta = REC_META[r.target];
              return (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={meta.color} icon={meta.icon as any} label={meta.label} />
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                      {r.title}
                    </Typography>
                    <Tooltip title="Impact estimé">
                      <Chip size="small" variant="outlined" color={LEVEL_COLOR[r.impact]} label={`Impact ${r.impact}`} />
                    </Tooltip>
                    <Tooltip title="Effort estimé">
                      <Chip size="small" variant="outlined" label={`Effort ${r.effort}`} />
                    </Tooltip>
                  </Stack>
                  {r.detail && (
                    <Typography variant="body2" color="text.secondary">
                      {r.detail}
                    </Typography>
                  )}
                  {r.target === 'content' && (
                    <Button
                      size="small"
                      sx={{ mt: 0.5 }}
                      disabled={savedIdeas.has(r.title)}
                      onClick={() => saveIdea(r.title, r.detail)}
                    >
                      {savedIdeas.has(r.title) ? '✓ Ajouté au contenu' : '→ Créer un contenu'}
                    </Button>
                  )}
                  {r.target === 'product' && (
                    <Button size="small" sx={{ mt: 0.5 }} onClick={() => navigate('/product/optimization')}>
                      → Optimiser la fiche
                    </Button>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      <Typography variant="caption" color="text.disabled">
        Synthèse calculée le {new Date(syn.computedAt).toLocaleString()}.
      </Typography>
    </Stack>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}
